import { useState } from "react";

import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import type { DragMoveEvent } from "@dnd-kit/core/dist/types/events";
import { horizontalListSortingStrategy, SortableContext } from "@dnd-kit/sortable";

import { Carousel } from "pro-features/components/Carousel/src/Carousel";
import type { PieChartDataSchema } from "app/pages/activation/components/GAWidget/types";

import type { GAGraphData } from "../../../types/api";
import type { BenchmarkGranularity } from "../../../types/common";
import Widget from "../Widget/Widget";
import { getWidgetsConfig } from "../widgetsConfig";

import * as SC from "./styles";

type SortableCarouselProps = {
    isSwSource?: boolean;
    items: string[];
    onDragEnd: (event: DragEndEvent) => void;
    onDragStart?: (event: DragStartEvent) => void;
    children: React.ReactNode[];
    margin?: number;
    forceHover?: boolean;
    dataAutomation?: string;
    data: Record<string, GAGraphData | PieChartDataSchema>;
    granularity: BenchmarkGranularity;
};

export const SortableCarousel = ({
    isSwSource,
    items,
    children,
    onDragEnd,
    onDragStart,
    margin,
    forceHover,
    dataAutomation,
    data,
    granularity,
}: SortableCarouselProps) => {
    const [activeId, setActiveId] = useState<string | null>(null);
    const [carouselIndex, setCarouselIndex] = useState<number | undefined>();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor),
    );

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveId(active.id as string);

        if (onDragStart) {
            onDragStart(event);
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveId(null);
        onDragEnd(event);
        setCarouselIndex(undefined);
    };

    const onDragMove = (event: DragMoveEvent) => {
        const { over } = event;

        if (over) {
            const newIndex = items.indexOf(over.id as string);
            if (newIndex !== carouselIndex) {
                setCarouselIndex(newIndex);
            }
        }
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragMove={onDragMove}
            autoScroll={false}
        >
            <SortableContext
                items={items}
                strategy={horizontalListSortingStrategy}
            >
                <Carousel
                    margin={margin}
                    forceHover={forceHover}
                    data-automation={dataAutomation}
                    selectedIndex={carouselIndex}
                >
                    {children}
                </Carousel>
            </SortableContext>

            <DragOverlay>
                {activeId ? (
                    <SC.DragOverlay isDragging>
                        <SC.DraggableIcon
                            isDragging
                            iconName="drag-indicator"
                            size="xs"
                        />
                        <Widget
                            metricName={activeId}
                            isConnected
                            isSwSource={isSwSource}
                            data={data[activeId]}
                            isLoading={false}
                            withBorder={false}
                            granularity={granularity}
                            {...getWidgetsConfig()[activeId]}
                        />
                    </SC.DragOverlay>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};
